import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
  uploadBytesResumable,
  getMetadata,
} from "firebase/storage";
import { storage } from "../firebase/config";
import { FILE_UPLOAD, STORAGE_PATHS, ERROR_MESSAGES } from "../utils/constants";

/**
 * Firebase Storage Service
 * Handles file upload, organization, and retrieval for the registration system
 * Files are organized by registration ID for easy management
 */
class StorageService {
  /**
   * Upload a file to Firebase Storage organized by registration ID
   * @param {File} file - The file to upload
   * @param {string} registrationId - The registration ID to organize files by
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<string>} - The download URL of the uploaded file
   */
  async uploadFile(file, registrationId, onProgress = null) {
    try {
      // Validate file before upload
      const validation = this.validateFile(file);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Generate file path organized by registration ID
      const filePath = this.generateFilePath(registrationId, file);
      const storageRef = ref(storage, filePath);

      let uploadTask;

      if (onProgress) {
        // Use resumable upload with progress tracking
        uploadTask = uploadBytesResumable(storageRef, file);

        return new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              onProgress(progress);
            },
            (error) => {
              reject(new Error(`Upload failed: ${error.message}`));
            },
            async () => {
              try {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref,
                );
                resolve(downloadURL);
              } catch (error) {
                reject(
                  new Error(`Failed to get download URL: ${error.message}`),
                );
              }
            },
          );
        });
      } else {
        // Simple upload without progress tracking
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        return downloadURL;
      }
    } catch (error) {
      throw new Error(`File upload failed: ${error.message}`);
    }
  }

  /**
   * Upload a receipt file for a registration
   * @param {File} file - The receipt file to upload
   * @param {string} registrationId - The registration ID
   * @param {Function} onProgress - Optional progress callback
   * @returns {Promise<string>} - The download URL of the uploaded receipt
   */
  async uploadReceipt(file, registrationId, onProgress = null) {
    return this.uploadFile(file, registrationId, onProgress);
  }

  /**
   * Get download URL for an existing file
   * @param {string} filePath - The path to the file in storage
   * @returns {Promise<string>} - The download URL
   */
  async getDownloadURL(filePath) {
    try {
      const storageRef = ref(storage, filePath);
      const downloadURL = await getDownloadURL(storageRef);
      return downloadURL;
    } catch (error) {
      throw new Error(`Failed to get download URL: ${error.message}`);
    }
  }

  /**
   * Get file metadata
   * @param {string} filePath - The path to the file in storage
   * @returns {Promise<Object>} - The file metadata
   */
  async getFileMetadata(filePath) {
    try {
      const storageRef = ref(storage, filePath);
      const metadata = await getMetadata(storageRef);
      return metadata;
    } catch (error) {
      throw new Error(`Failed to get file metadata: ${error.message}`);
    }
  }

  /**
   * Delete a file from Firebase Storage
   * @param {string} filePath - The path to the file to delete
   * @returns {Promise<void>}
   */
  async deleteFile(filePath) {
    try {
      const storageRef = ref(storage, filePath);
      await deleteObject(storageRef);
    } catch (error) {
      throw new Error(`File deletion failed: ${error.message}`);
    }
  }

  /**
   * Delete a receipt file for a registration
   * @param {string} registrationId - The registration ID
   * @param {string} fileName - The file name (optional, defaults to 'receipt')
   * @returns {Promise<void>}
   */
  async deleteReceipt(registrationId, fileName = "receipt") {
    const filePath = `${STORAGE_PATHS.RECEIPTS}/${registrationId}/${fileName}`;
    return this.deleteFile(filePath);
  }

  /**
   * Validate file type and size
   * @param {File} file - The file to validate
   * @returns {Object} - Validation result with isValid and error properties
   */
  validateFile(file) {
    if (!file) {
      return {
        isValid: false,
        error: "No file provided",
      };
    }

    // Check file size
    if (file.size > FILE_UPLOAD.MAX_SIZE_BYTES) {
      return {
        isValid: false,
        error: ERROR_MESSAGES.FILE_TOO_LARGE,
      };
    }

    // Check file type
    if (!FILE_UPLOAD.ALLOWED_TYPES.includes(file.type)) {
      return {
        isValid: false,
        error: ERROR_MESSAGES.INVALID_FILE_TYPE,
      };
    }

    return {
      isValid: true,
      error: null,
    };
  }

  /**
   * Generate organized file path by registration ID
   * @param {string} registrationId - The registration ID
   * @param {File} file - The file to generate path for
   * @returns {string} - The generated file path
   */
  generateFilePath(registrationId, file) {
    // Extract file extension
    const fileExtension = this.getFileExtension(file);

    // Generate timestamp for uniqueness
    const timestamp = Date.now();

    // Create organized path: receipts/{registrationId}/receipt_{timestamp}.{extension}
    return `${STORAGE_PATHS.RECEIPTS}/${registrationId}/receipt_${timestamp}${fileExtension}`;
  }

  /**
   * Get file extension from file object
   * @param {File} file - The file object
   * @returns {string} - The file extension with dot (e.g., '.pdf', '.jpg')
   */
  getFileExtension(file) {
    const fileName = file.name;
    const lastDotIndex = fileName.lastIndexOf(".");

    if (lastDotIndex === -1 || lastDotIndex === fileName.length - 1) {
      // No extension found or dot is at the end, determine from MIME type
      const mimeTypeMap = {
        "image/jpeg": ".jpg",
        "image/png": ".png",
        "image/gif": ".gif",
        "application/pdf": ".pdf",
      };
      return mimeTypeMap[file.type] || ".bin";
    }

    const extension = fileName.substring(lastDotIndex);
    // Validate extension contains only valid characters
    if (/^\.[\w]+$/.test(extension)) {
      return extension;
    }

    // If extension has invalid characters, fall back to MIME type
    const mimeTypeMap = {
      "image/jpeg": ".jpg",
      "image/png": ".png",
      "image/gif": ".gif",
      "application/pdf": ".pdf",
    };
    return mimeTypeMap[file.type] || ".bin";
  }

  /**
   * Extract registration ID from file path
   * @param {string} filePath - The file path
   * @returns {string|null} - The registration ID or null if not found
   */
  extractRegistrationIdFromPath(filePath) {
    const pathParts = filePath.split("/");
    if (pathParts.length >= 3 && pathParts[0] === STORAGE_PATHS.RECEIPTS) {
      return pathParts[1];
    }
    return null;
  }

  /**
   * Check if file exists in storage
   * @param {string} filePath - The path to check
   * @returns {Promise<boolean>} - True if file exists, false otherwise
   */
  async fileExists(filePath) {
    try {
      await this.getFileMetadata(filePath);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Get file size in human readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} - Human readable file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }
}

export default new StorageService();
